/**
 * Cross-chain USDT transfer service using Ledger hardware wallet.
 * All functions require a live Ledger transport (opened by the caller).
 * No automated signing — Ledger button press is mandatory for every transfer.
 */
import BigNumber from 'bignumber.js';

const TRON_RPC = 'https://api.trongrid.io';
const USDT_TRC20 = 'TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t';

const CHAIN_PARAMS = {
  ethereum: { chainId: 1,   rpc: 'https://ethereum-rpc.publicnode.com', usdtAddr: '0xdAC17F958D2ee523a2206206994597C13D831ec7', decimals: 6 },
  bsc:      { chainId: 56,  rpc: 'https://bsc-dataseed.binance.org',    usdtAddr: '0x55d398326f99059fF775485246999027B3197955', decimals: 18 },
  polygon:  { chainId: 137, rpc: 'https://polygon-rpc.com',            usdtAddr: '0xc2132D05D31c914a87C6611C10748AEb04B58e8F', decimals: 6 },
};

const ERC20_ABI = [
  'function transfer(address to, uint256 amount) returns (bool)',
];

/**
 * Send USDT on Tron (TRC20) via Ledger.
 *
 * @param {object} opts
 * @param {string}   opts.fromAddress  - Base58 Tron source address
 * @param {string}   opts.toAddress    - Base58 Tron destination address
 * @param {string|number} opts.amount  - Human-readable USDT amount (e.g. "100.5")
 * @param {number}   opts.hdIndex      - Ledger account index (0-based)
 * @param {object}   opts.transport    - Open Ledger transport
 * @param {Function} [opts.onStatus]   - Callback for status updates
 * @returns {Promise<{ txid: string, success: boolean }>}
 */
export async function sendUsdtTron({ fromAddress, toAddress, amount, hdIndex, transport, onStatus }) {
  const status = onStatus || (() => {});
  const Trx = (await import('@ledgerhq/hw-app-trx')).default;
  const TronWeb = (await import('tronweb')).default;

  const tronWeb = new TronWeb({ fullNode: TRON_RPC, solidityNode: TRON_RPC });
  tronWeb.setAddress(fromAddress);

  const valueRaw = new BigNumber(amount).multipliedBy(new BigNumber(10).pow(6)).toFixed(0);
  const parameter = [
    { type: 'address', value: toAddress },
    { type: 'uint256', value: valueRaw },
  ];

  const txObj = await tronWeb.transactionBuilder.triggerSmartContract(
    tronWeb.address.toHex(USDT_TRC20),
    'transfer(address,uint256)',
    {},
    parameter,
    tronWeb.address.toHex(fromAddress)
  );

  if (!txObj || !txObj.transaction) throw new Error('Failed to build Tron TRC20 transaction');

  const trx = new Trx(transport);
  const hdPath = `44'/195'/0'/0/${hdIndex}`;

  status('ledger', 'Waiting for Ledger approval…');
  const sign = await trx.signTransaction(hdPath, txObj.transaction.raw_data_hex, [], 105);

  const signedTx = { ...txObj.transaction, signature: [sign] };

  status('broadcast', 'Broadcasting to Tron network…');
  const receipt = await tronWeb.trx.sendRawTransaction(signedTx);

  if (!receipt.result) throw new Error(receipt.message || 'Tron broadcast failed');
  return { txid: receipt.txid, success: true };
}

/**
 * Send USDT on an EVM chain (Ethereum / BSC / Polygon) via Ledger.
 *
 * @param {object} opts
 * @param {string}   opts.fromAddress  - 0x source address
 * @param {string}   opts.toAddress    - 0x destination address
 * @param {string|number} opts.amount  - Human-readable USDT amount
 * @param {'ethereum'|'bsc'|'polygon'} opts.chain
 * @param {number}   opts.hdIndex      - Ledger account index (0-based)
 * @param {object}   opts.transport    - Open Ledger transport
 * @param {number}   [opts.gasPriceGwei] - Gas price override (default: network estimate)
 * @param {number}   [opts.gasLimit]     - Gas limit override (default: 90000)
 * @param {Function} [opts.onStatus]   - Callback for status updates
 * @returns {Promise<{ txid: string, success: boolean }>}
 */
export async function sendUsdtEvm({ fromAddress, toAddress, amount, chain, hdIndex, transport, gasPriceGwei, gasLimit, onStatus }) {
  const status = onStatus || (() => {});
  const { ethers } = await import('ethers');
  const Eth = (await import('@ledgerhq/hw-app-eth')).default;

  const params = CHAIN_PARAMS[chain];
  if (!params) throw new Error(`Unsupported chain: ${chain}`);

  const provider = new ethers.providers.JsonRpcProvider(params.rpc);
  const ethApp = new Eth(transport);
  const hdPath = `44'/60'/0'/0/${hdIndex}`;

  const nonce = await provider.getTransactionCount(fromAddress, 'latest');
  const network = await provider.getNetwork();

  const gasPriceWei = gasPriceGwei
    ? ethers.utils.parseUnits(String(gasPriceGwei), 'gwei')
    : await provider.getGasPrice();

  const amountRaw = new BigNumber(amount)
    .multipliedBy(new BigNumber(10).pow(params.decimals))
    .toFixed(0);

  const iface = new ethers.utils.Interface(ERC20_ABI);
  const data = iface.encodeFunctionData('transfer', [
    toAddress,
    ethers.BigNumber.from(amountRaw),
  ]);

  const txData = {
    to:       params.usdtAddr,
    value:    '0x0',
    gasPrice: gasPriceWei.toHexString(),
    gasLimit: ethers.BigNumber.from(gasLimit || 90000).toHexString(),
    nonce:    ethers.utils.hexlify(nonce),
    chainId:  network.chainId,
    data,
  };

  const unsignedTx = ethers.utils.serializeTransaction(txData);
  const txHex = unsignedTx.slice(2);

  status('ledger', 'Waiting for Ledger approval…');
  const sig = await ethApp.signTransaction(hdPath, txHex, null);

  const signedTx = ethers.utils.serializeTransaction(txData, {
    v: parseInt(sig.v, 16),
    r: '0x' + sig.r,
    s: '0x' + sig.s,
  });

  status('broadcast', 'Broadcasting to ' + chain + ' network…');
  const response = await provider.sendTransaction(signedTx);

  response.wait(1).catch(() => {});
  return { txid: response.hash, success: true };
}

/**
 * Unified entry point — dispatches to Tron or EVM based on chain.
 */
export async function sendUsdt({ chain, ...opts }) {
  if (chain === 'tron') return sendUsdtTron(opts);
  return sendUsdtEvm({ chain, ...opts });
}

/**
 * Open a Ledger transport (WebHID → WebUSB fallback).
 * Returns the transport; caller must close it when done.
 */
export async function openLedgerTransport() {
  const TransportWebHID = (await import('@ledgerhq/hw-transport-webhid')).default;
  const TransportWebUSB = (await import('@ledgerhq/hw-transport-webusb')).default;
  try {
    if (await TransportWebHID.isSupported()) return await TransportWebHID.create();
  } catch (_) {}
  return await TransportWebUSB.create();
}
